import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Link, useNavigate } from "@tanstack/react-router"
import { Form, InputField } from "@mimber/forms"
import { Button, message, Divider, Typography } from "@mimber/ui-web"
import { SocialLoginButton, KeyOutlined } from "@mimber/ui-web"
import { useAuth } from "@mimber/auth"
import {
  loginSchema,
  mapCognitoError,
  signInWithEmail,
  signInWithPasskey,
} from "@mimber/auth"
import { fetchAuthSession } from "aws-amplify/auth"
import { MFAChallengeForm } from "../MFAChallengeForm"
import { NewPasswordRequiredForm } from "../NewPasswordRequiredForm"
import { RecoveryCodeLoginForm } from "../RecoveryCodeLoginForm"
import { MFAMethodSelector } from "../../blocks/MFAMethodSelector"
import { handleSignInStep } from "@mimber/auth"
import type { ChallengeState } from "@mimber/auth"
import type { LoginFormProps, LoginFormData } from "./LoginForm.types"
import type { SignInOutput } from "aws-amplify/auth"

const { Text } = Typography

export const LoginForm: React.FunctionComponent<LoginFormProps> = ({
  onError,
  onUserNotConfirmed,
}) => {
  const { loginWithRedirect } = useAuth()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [challengeState, setChallengeState] = useState<ChallengeState>({
    step: "credentials",
  })
  const [email, setEmail] = useState("")

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  const processSignInResult = async (
    result: SignInOutput,
    currentEmail: string
  ) => {
    const outcome = handleSignInStep(result)

    if (outcome === "done") {
      await fetchAuthSession({ forceRefresh: true })
      return
    }

    if (outcome === "confirm-sign-up") {
      message.warning("Please verify your email before logging in.")
      onUserNotConfirmed?.(currentEmail)
      return
    }

    if (outcome === "reset-password") {
      message.info("You need to reset your password.")
      navigate({ to: "/forgot-password" })
      return
    }

    setChallengeState(outcome)

    if (outcome.step === "error") {
      message.error(outcome.message)
    }
  }

  const handleSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    setEmail(data.email)
    try {
      const result = await signInWithEmail(data.email, data.password)
      await processSignInResult(result, data.email)
    } catch (error) {
      const errorMessage = mapCognitoError(error)
      message.error(errorMessage)
      onError?.(error as Error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleMFASuccess = async () => {
    // MFA verification done, session is refreshed inside MFAChallengeForm
  }

  const handleNewPasswordSuccess = async () => {
    // Password set, session is refreshed inside NewPasswordRequiredForm
  }

  const handleRecoveryCodeSuccess = () => {
    message.info("Please sign in again with your credentials.")
    setChallengeState({ step: "credentials" })
    form.reset()
  }

  const handleCancel = () => {
    setChallengeState({ step: "credentials" })
    form.reset()
  }

  const handleUseRecoveryCode = () => {
    setChallengeState({ step: "recovery-code" })
  }

  const handleGoogleLogin = () => {
    loginWithRedirect({ provider: "Google" })
  }

  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false)

  const handlePasskeyLogin = async () => {
    setIsPasskeyLoading(true)
    try {
      const result = await signInWithPasskey()
      await processSignInResult(result, "")
    } catch (error) {
      if (error instanceof Error && error.name === "NotAllowedError") {
        // User cancelled the biometric prompt — do nothing
        return
      }
      if (
        error instanceof Error &&
        error.message.includes("RelyingPartyMismatch")
      ) {
        message.error(
          "Passkey sign-in is not supported on localhost. Deploy to a *.mimber.com domain to test."
        )
        return
      }
      const errorMessage = mapCognitoError(error)
      message.error(errorMessage)
      onError?.(error as Error)
    } finally {
      setIsPasskeyLoading(false)
    }
  }

  if (challengeState.step === "mfa-code") {
    return (
      <MFAChallengeForm
        method={challengeState.method}
        onSuccess={handleMFASuccess}
        onCancel={handleCancel}
        onUseRecoveryCode={handleUseRecoveryCode}
      />
    )
  }

  if (challengeState.step === "mfa-selection") {
    return (
      <MFAMethodSelector
        allowedMethods={challengeState.allowedMethods}
        onMethodSelected={(method) => {
          setChallengeState({ step: "mfa-code", method })
        }}
        onCancel={handleCancel}
        onUseRecoveryCode={handleUseRecoveryCode}
      />
    )
  }

  if (challengeState.step === "new-password-required") {
    return (
      <NewPasswordRequiredForm
        onSuccess={handleNewPasswordSuccess}
        onCancel={handleCancel}
      />
    )
  }

  if (challengeState.step === "recovery-code") {
    return (
      <RecoveryCodeLoginForm
        email={email}
        onSuccess={handleRecoveryCodeSuccess}
        onCancel={handleCancel}
      />
    )
  }

  return (
    <>
      <Form form={form} onSubmit={handleSubmit}>
        <InputField
          name="email"
          type="email"
          label="Email"
          placeholder="Enter your email"
          required
        />
        <InputField
          name="password"
          type="password"
          label="Password"
          placeholder="Enter your password"
          required
        />
        <div className="text-right mb-4">
          <Link to="/forgot-password">Forgot password?</Link>
        </div>
        <Button
          type="primary"
          htmlType="submit"
          loading={isLoading}
          className="w-full mb-4"
        >
          Log In
        </Button>
      </Form>

      <Divider>
        <Text>or</Text>
      </Divider>

      <SocialLoginButton provider="Google" onClick={handleGoogleLogin} />

      <Button
        icon={<KeyOutlined />}
        onClick={handlePasskeyLogin}
        loading={isPasskeyLoading}
        className="w-full mt-3"
      >
        Sign in with a Passkey
      </Button>
    </>
  )
}
